import { useEffect, useState } from "react";
import { api, STATUSES, PLATFORMS, OCCURRENCES } from "@/lib/api";
import { ImageOff } from "lucide-react";

const STATUS_CLS = {
  em_analise: "bg-blue-50 text-blue-700 border-blue-200",
  preparada: "bg-amber-50 text-amber-800 border-amber-200",
  enviada: "bg-slate-100 text-slate-700 border-slate-300",
  aprovada: "bg-emerald-50 text-emerald-700 border-emerald-200",
  negada: "bg-red-50 text-red-700 border-red-200",
  aguardando_provas: "bg-orange-50 text-orange-700 border-orange-200",
};

export const StatusBadge = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLS[status] || STATUS_CLS.em_analise}`}>
    {STATUSES[status] || status}
  </span>
);

const PLAT_CLS = { "99food": "bg-amber-50 text-amber-700 border-amber-300", ifood: "bg-red-50 text-red-600 border-red-300", keeta: "bg-yellow-50 text-yellow-800 border-yellow-400", outra: "bg-slate-50 text-slate-600 border-slate-300" };
export const PlatformBadge = ({ platform }) => (
  <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-bold ${PLAT_CLS[platform] || PLAT_CLS.outra}`}>{PLATFORMS[platform] || platform}</span>
);

const STAR_SLOTS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

export const StrengthStars = ({ value = 0, size = "text-2xl" }) => (
  <div data-testid="strength-score" className="flex items-center gap-3 flex-wrap">
    <span className={`${size} tracking-tight leading-none`} aria-label={`${value} de 10`}>
      {STAR_SLOTS.map((n) => (
        <span key={n} className={n < value ? "text-amber-500" : "text-slate-300"}>★</span>
      ))}
    </span>
    <span className="font-display text-2xl font-extrabold text-slate-900">{value}/10</span>
  </div>
);

export function AuthImage({ fileId, className, alt = "evidência" }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let url;
    api.get(`/files/${fileId}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setErr(true));
    return () => url && URL.revokeObjectURL(url);
  }, [fileId]);
  if (err) return <div className={`${className} flex items-center justify-center bg-slate-100 text-slate-400`}><ImageOff className="h-6 w-6" /></div>;
  if (!src) return <div className={`${className} animate-pulse bg-slate-100`} />;
  return <img src={src} alt={alt} className={className} />;
}

export const PageHeader = ({ eyebrow, title, desc, children }) => (
  <div className="mb-8 flex flex-col gap-4 border-b border-slate-200 pb-6 md:flex-row md:items-end md:justify-between">
    <div>
      {eyebrow && <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-amber-500"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" />{eyebrow}</p>}
      <h1 className="font-display mt-1.5 text-3xl font-extrabold text-slate-900 sm:text-4xl">{title}</h1>
      {desc && <p className="mt-2 max-w-2xl text-sm text-slate-500 md:text-base">{desc}</p>}
    </div>
    {children}
  </div>
);

export const Panel = ({ title, sub, icon: Icon, children, className = "", testid }) => (
  <section data-testid={testid} className={`rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 ${className}`}>
    {title && (
      <div className="mb-5 flex items-start gap-3">
        {Icon && <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100"><Icon className="h-5 w-5 text-slate-900" /></span>}
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
          {sub && <p className="text-xs text-slate-500">{sub}</p>}
        </div>
      </div>
    )}
    {children}
  </section>
);

const Step = ({ n, label, value, testid }) => (
  <li className="flex gap-3">
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-white">{n}</span>
    <div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p><p className="text-sm font-bold text-slate-900" data-testid={testid}>{value}</p></div>
  </li>
);

export const FillGuide = ({ platform, occurrence, motivo, submotivo }) => (
  <div data-testid="fill-guide" className="rounded-2xl border-2 border-slate-900 bg-white p-4">
    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-600">O que preencher na {PLATFORMS[platform] || platform} · {OCCURRENCES[occurrence] || occurrence}</p>
    <ol className="mt-3 space-y-3">
      <Step n={1} label="Motivo" value={motivo || "Outros"} testid="fill-motivo" />
      <Step n={2} label="Submotivo" value={submotivo || "Sem submotivo"} testid="fill-submotivo" />
      <Step n={3} label="Descrição" value="Cole o texto da contestação (botão COPIAR)" />
    </ol>
  </div>
);

const ringColor = (p) => (p >= 70 ? "#2F7A5B" : p >= 50 ? "#E39A33" : "#FF6A0F");

export const ScoreRing = ({ pct, size = 44, stroke = 4, label, testid }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = pct ?? 0;
  return (
    <div data-testid={testid} className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#ECE8DD" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={ringColor(v)} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (c * v) / 100} style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display font-extrabold" style={{ color: ringColor(v), fontSize: size * 0.26 }}>{pct == null ? "—" : `${v}%`}</span>
        {label && <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>}
      </div>
    </div>
  );
};
